import React from 'react';
import { Box, Paper, Skeleton, Grid } from '@mui/material';

/**
 * SearchSkeleton - Loading skeleton for search results pages
 * Displays placeholder cards while data is loading to improve perceived performance.
 *
 * @param {Object} props
 * @param {number} props.count - Number of skeleton items to show (default: 5)
 * @param {boolean} props.showFacets - Whether to show facet panel skeleton (default: true)
 * @param {string} props.variant - 'card' or 'list' style (default: 'list')
 */
export default function SearchSkeleton({ count = 5, showFacets = true, variant = 'list' }) {
  const renderListSkeleton = () => (
    <Box>
      {[...Array(count)].map((_, index) => (
        <Box key={index} sx={{ py: 2, borderBottom: index < count - 1 ? '1px solid #eee' : 'none' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            {/* Avatar skeleton */}
            <Skeleton variant="circular" width={48} height={48} />

            <Box sx={{ flex: 1 }}>
              {/* Title and chips */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                <Skeleton variant="text" width={180} height={32} />
                <Skeleton variant="rounded" width={60} height={24} />
                <Skeleton variant="rounded" width={80} height={24} />
              </Box>

              {/* Subtitle */}
              <Skeleton variant="text" width="60%" height={20} />

              {/* Metadata line */}
              <Skeleton variant="text" width="40%" height={16} sx={{ mt: 0.5 }} />

              {/* Content preview */}
              <Skeleton variant="text" width="100%" height={16} sx={{ mt: 1 }} />
              <Skeleton variant="text" width="95%" height={16} />
              <Skeleton variant="text" width="80%" height={16} />
            </Box>
          </Box>
        </Box>
      ))}
    </Box>
  );

  const renderCardSkeleton = () => (
    <Grid container spacing={2}>
      {[...Array(count)].map((_, index) => (
        <Grid item xs={12} sm={6} md={4} key={index}>
          <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
            {/* Header with icon/avatar */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Skeleton variant="circular" width={40} height={40} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="80%" height={24} />
                <Skeleton variant="text" width="50%" height={16} />
              </Box>
            </Box>

            {/* Card content */}
            <Skeleton variant="text" width="100%" height={16} />
            <Skeleton variant="text" width="90%" height={16} />
            <Skeleton variant="text" width="70%" height={16} />

            {/* Chips/tags */}
            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              <Skeleton variant="rounded" width={60} height={24} />
              <Skeleton variant="rounded" width={80} height={24} />
            </Box>
          </Paper>
        </Grid>
      ))}
    </Grid>
  );

  const renderFacetsSkeleton = () => (
    <Paper elevation={1} sx={{ p: 2, borderRadius: 3 }}>
      {/* Filter header */}
      <Skeleton variant="text" width={100} height={28} sx={{ mb: 2 }} />

      {/* Facet sections */}
      {[...Array(4)].map((_, sectionIndex) => (
        <Box key={sectionIndex} sx={{ mb: 2 }}>
          <Skeleton variant="text" width={80} height={24} sx={{ mb: 1 }} />
          {[...Array(5)].map((_, itemIndex) => (
            <Box key={itemIndex} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
              <Skeleton variant="rectangular" width={18} height={18} />
              <Skeleton variant="text" width={`${60 + Math.random() * 30}%`} height={20} />
            </Box>
          ))}
        </Box>
      ))}
    </Paper>
  );

  return (
    <Grid container spacing={3}>
      {showFacets && (
        <Grid item xs={12} md={3}>
          {renderFacetsSkeleton()}
        </Grid>
      )}

      <Grid item xs={12} md={showFacets ? 9 : 12}>
        <Paper elevation={1} sx={{ p: 2, borderRadius: 3 }}>
          {/* Results count skeleton */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Skeleton variant="text" width={150} height={28} />
            <Skeleton variant="rounded" width={200} height={32} />
          </Box>

          {variant === 'list' ? renderListSkeleton() : renderCardSkeleton()}
        </Paper>
      </Grid>
    </Grid>
  );
}
